import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { AdminTabs } from 'src/components/admin/types'
import { useAuthProvider } from 'src/context/AuthProvider'
import type { ILocation, IQuery, IQueryTeamArgs } from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { getHostNames } from 'src/utils/profile'

/**
 * Admin-only team fetch. `getStaticProps` strips `location.team` from the
 * props of every listing that does NOT disclose its host (Broker /
 * Traditional / Fleet — see `listingDisclosesHost`), because the operator's
 * name would otherwise ship inside `__NEXT_DATA__` on exactly the listings
 * that must stay anonymous. That strip left THIS panel — the one surface
 * that legitimately needs the team — showing the submitter's name (usually
 * a SpotMe admin) as "Dueño del espacio" on every anonymous listing.
 *
 * So the panel asks for the team itself, behind the same `isAdmin` gate
 * that renders it: renters still get nothing in their payload, admins get
 * the operator back. Do NOT "fix" this by un-stripping the props.
 */
const GET_LISTING_TEAM = gql`
  query AdminListingTeam($teamId: String!) {
    team(teamId: $teamId) {
      id
      name
    }
  }
`

/** Collapse preference — per browser, shared across every listing page. */
const EXPANDED_STORAGE_KEY = 'spotme.adminOwnerPanel.expanded'
/**
 * Static id, not `useId()`. One panel per page (admin-only), and `useId`
 * drifts between SSR and client on this page — see the Trigger K notes in
 * `CustomFilterDropdown`. A constant can't drift.
 */
const DETAILS_ID = 'admin-owner-panel-details'

type Props = {
  location: ILocation
}

/**
 * Admin-only "Owner panel" on listing detail pages.
 *
 * Surfaces who owns the space (host name + contact + verification + team
 * affiliation) to SpotMe admins regardless of the variant's `sections.user`
 * config. Pre-2026-05-26 the host masthead in <User> was the only owner
 * affordance on listing pages; after Broker/Traditional flipped their
 * `user.variant` to `'specialist'` / `'hidden'`, admins lost the ability
 * to see who runs the listing without leaving the page to the admin
 * dashboard.
 *
 * Render gate: ONLY for admins (`currentUser.isAdmin`). The component
 * returns null for every other viewer, so this is purely additive — no
 * renter, no logged-out visitor, no host of a different listing sees
 * anything.
 *
 * Placement (moved 2026-08-26): rendered from `Header.tsx`, directly under
 * the configuration selector — near the top of the editorial rail so the
 * admin team finds it without hunting (Fer opens listing URLs all day and
 * needs "who owns this / can I WA them / open it in admin"), but below the
 * gallery so it never displaces the listing itself. It sat above the photos
 * for one iteration; too intrusive. Living inside Header also means it
 * renders on every variant, including the Traditional one whose
 * `sections.user.variant === 'hidden'` short-circuits the User block — that
 * block is NOT where this belongs any more.
 *
 * It is a compact bar, not a card: the summary row carries the two facts
 * (owner, KYC) and the two actions (WhatsApp the host, open in admin) that
 * motivate the visit; everything else lives behind the toggle, whose state
 * persists in localStorage so the choice sticks across listings.
 *
 * Mirrors the field set surfaced on the admin location-verifications
 * tab's `LocationRequestDisplay` (name, email, phone, host-verified
 * badge, host link, team) so a /dashboard/admin viewer and a public-URL
 * admin viewer see the same identity data without context switching.
 */
export default function AdminOwnerPanel({ location }: Props) {
  const { t } = useTranslation('details')
  const { currentUser } = useAuthProvider()

  // Hook runs before the admin early-return (rules of hooks) but `skip`
  // keeps it inert for non-admins and team-less listings — no request, no
  // team data in any non-admin client.
  const isAdmin = !!currentUser?.isAdmin
  const { data: teamData } = useQuery<Pick<IQuery, 'team'>, IQueryTeamArgs>(
    GET_LISTING_TEAM,
    {
      variables: { teamId: location.teamId as string },
      // Monthly (host-disclosing) listings keep `team` in their props, so
      // only the stripped ones need the round-trip.
      skip: !isAdmin || !location.teamId || !!location.team
    }
  )

  // Expanded by default — collapsing is opt-in, so nothing that used to be
  // visible disappears on first load. The stored preference is read after
  // mount (never during render) so a Safari-private-mode throw or a stale
  // value can't produce an SSR/client markup mismatch on a page with a
  // documented hydration-error history.
  const [isExpanded, setIsExpanded] = useState(true)

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(EXPANDED_STORAGE_KEY)
      if (stored !== null) setIsExpanded(stored === '1')
    } catch {
      // Storage disabled (private mode / blocked cookies). The default
      // above is the correct fallback — a lost UI preference is not worth
      // surfacing to the admin.
    }
  }, [])

  const handleToggle = () => {
    const next = !isExpanded
    setIsExpanded(next)
    try {
      window.localStorage.setItem(EXPANDED_STORAGE_KEY, next ? '1' : '0')
    } catch {
      // Same as above — the toggle still works for this page view.
    }
  }

  if (!isAdmin) return null

  const { user, submittedOn } = location
  // `location.team` is no longer in the page payload (see GET_LISTING_TEAM
  // above), so the team comes from the admin-only fetch. Falling back to
  // `location.team` keeps any caller that still passes a hydrated location
  // (admin dashboard lists) working unchanged.
  const team = teamData?.team ?? location.team
  // Team-first: the landlord IS the team when there is one — the submitter is
  // just the human who typed the listing in (very often a SpotMe admin). This
  // used to read `fullName || team?.name`, which put the admin's own name at
  // the top of every team-owned listing.
  const { name: displayName, personName: fullName } = getHostNames(
    { ...location, team },
    t('ADMIN_OWNER.UNKNOWN_HOST')
  )
  const isHostVerified = !!user?.isVerified

  // The location-verifications tab only ever holds listings AWAITING review,
  // so deep-linking a PUBLISHED listing there dropped the admin on a queue
  // that could never contain it. Send verified listings to the listing's own
  // dashboard page instead (admins can read any listing), scoped to its team
  // so the page's back-link lands somewhere sensible.
  const adminListingHref = location.isVerified
    ? `/dashboard/host/listings/${location.id}${
        location.teamId ? `?teamId=${location.teamId}` : ''
      }`
    : `${RedirectUrls.Admin}?tab=${AdminTabs.LocationVerifications}&selected=${location.id}`
  // User search matches on name / email / phone — NOT on uid, which is what
  // this used to pass, so the tab opened with nothing found. Send the email
  // (falling back to the person's name) as the prefilled term.
  const hostSearchTerm = user?.email || fullName || null
  const adminUserHref = hostSearchTerm
    ? `${RedirectUrls.Admin}?tab=${AdminTabs.UserSearch}&selected=${encodeURIComponent(hostSearchTerm)}`
    : null
  const adminTeamHref = team?.id ? `/dashboard/host/team/${team.id}` : null

  const phoneDigits = user?.phoneNumber
    ? user.phoneNumber.replace(/[^\d]/g, '')
    : ''

  const factLabelClass =
    'shrink-0 font-display text-[10px] tracking-[0.16em] text-ink-200 uppercase md:text-[11px]'
  const quickActionClass =
    'inline-flex grow items-center justify-center gap-2 border bg-white-500 px-3 py-1.5 text-[11px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 lg:grow-0'
  const detailLinkClass =
    'inline-flex items-center justify-center gap-2 border border-ink-100/40 bg-white-500 px-4 py-2 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800'

  return (
    <aside
      aria-label={t('ADMIN_OWNER.EYEBROW')}
      // In-rail block: the sections around it carry their own hairlines
      // (`<SectionDivider />`, LocationFeatures' internal rule), so this
      // only owns the breathing room on either side of its own border box.
      className="mt-8 mb-2 border border-orange-300 bg-orange-100/40 md:mt-10"
    >
      {/* Summary bar — always visible, whatever the toggle says. Carries the
          two facts an admin opens a listing for (who owns it, is the host
          KYC'd) plus the two actions they take most (WhatsApp the host,
          open the listing in admin), so a collapsed panel is still useful
          on its own. */}
      <div className="flex flex-col gap-3 px-4 py-3 md:px-6 md:py-4 lg:flex-row lg:items-center lg:gap-6">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={DETAILS_ID}
          className="flex min-w-0 flex-1 items-start gap-3 text-left lg:items-center"
        >
          <i
            aria-hidden="true"
            className="si si-eye mt-1 shrink-0 text-[13px] text-orange-500 lg:mt-0"
          />
          {/* Name + KYC stack on mobile and sit on one line at md:. Keeping
              them on a single 320px row truncated the owner name to
              "Alma…" — the one fact the bar exists to show. */}
          <span className="flex min-w-0 flex-1 flex-col gap-1.5 lg:flex-row lg:items-center lg:gap-3">
            <span className="hidden shrink-0 font-display text-[10px] tracking-[0.22em] text-ink-700 uppercase sm:inline md:text-[11px]">
              {t('ADMIN_OWNER.EYEBROW_SHORT')}
            </span>
            <span className="min-w-0 font-display text-[15px] leading-tight font-medium [overflow-wrap:anywhere] text-ink-700 md:text-[17px] lg:line-clamp-2 lg:flex-1">
              {displayName}
              {fullName && (
                <span className="ml-2 font-sans text-[11px] tracking-[0.16em] text-ink-200 uppercase">
                  · {fullName}
                </span>
              )}
            </span>
            <span
              className={`self-start border px-2 py-0.5 font-display text-[10px] tracking-[0.14em] uppercase lg:shrink-0 lg:self-auto ${
                isHostVerified
                  ? 'border-green-700/40 text-green-700'
                  : 'border-orange-500/50 text-ink-700'
              }`}
            >
              {t('ADMIN_OWNER.KYC')} ·{' '}
              {isHostVerified
                ? t('ADMIN_OWNER.KYC_VERIFIED')
                : t('ADMIN_OWNER.KYC_UNVERIFIED')}
            </span>
          </span>
          {/* Transform-only rotation — no layout property animated. */}
          <i
            aria-hidden="true"
            className={`si si-down mt-1 shrink-0 text-[11px] text-orange-500 transition-transform duration-200 lg:mt-0 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {phoneDigits && (
            <a
              href={`https://wa.me/${phoneDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${quickActionClass} border-ink-100/40`}
            >
              <i aria-hidden="true" className="si si-chat text-[12px]" />
              {user?.phoneNumber}
            </a>
          )}
          <Link
            href={adminListingHref}
            className={`${quickActionClass} border-orange-300`}
          >
            <i aria-hidden="true" className="si si-edit text-[12px]" />
            {t('ADMIN_OWNER.OPEN_IN_ADMIN')}
          </Link>
        </div>
      </div>

      {/* Always rendered, hidden with `display: none` when collapsed — an
          `aria-controls` target that only exists half the time is a dangling
          reference, and `hidden` takes the block out of the a11y tree
          anyway. */}
      <div
        id={DETAILS_ID}
        className={`border-t border-orange-300/60 px-4 pt-4 pb-4 md:px-6 md:pt-5 md:pb-5 ${
          isExpanded ? '' : 'hidden'
        }`}
      >
        {/* Quick-fact grid — same shape as LocationRequestDisplay's quick
              strip so admins can compare against the verification tab
              without re-reading layout. KYC is deliberately absent: the
              summary chip above already carries it. */}
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-[13px] md:grid-cols-2 md:text-sm">
          {user?.email && (
            <div className="flex items-baseline gap-2">
              <dt className={factLabelClass}>{t('ADMIN_OWNER.EMAIL')}</dt>
              <dd className="min-w-0 flex-1 truncate text-ink-700">
                <a
                  href={`mailto:${user.email}`}
                  className="hover:text-blue-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  {user.email}
                </a>
              </dd>
            </div>
          )}
          {user?.phoneNumber && (
            <div className="flex items-baseline gap-2">
              <dt className={factLabelClass}>{t('ADMIN_OWNER.PHONE')}</dt>
              <dd className="min-w-0 flex-1 truncate text-ink-700">
                <a
                  href={`tel:${user.phoneNumber}`}
                  className="hover:text-blue-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  {user.phoneNumber}
                </a>
              </dd>
            </div>
          )}
          {submittedOn && (
            <div className="flex items-baseline gap-2">
              <dt className={factLabelClass}>{t('ADMIN_OWNER.SUBMITTED')}</dt>
              <dd className="min-w-0 flex-1 text-ink-700">
                {new Date(submittedOn).toLocaleDateString('es-MX', {
                  dateStyle: 'medium'
                })}
              </dd>
            </div>
          )}
          {team && (
            <div className="flex items-baseline gap-2">
              <dt className={factLabelClass}>{t('ADMIN_OWNER.TEAM')}</dt>
              <dd className="min-w-0 flex-1 text-ink-700">
                {team.name}
                {team.id && (
                  <>
                    {' · '}
                    <code className="rounded bg-cream-300 px-1.5 py-0.5 text-[11px] text-ink-200">
                      {team.id.slice(0, 8)}…
                    </code>
                  </>
                )}
              </dd>
            </div>
          )}
          <div className="flex items-baseline gap-2 md:col-span-2">
            <dt className={factLabelClass}>{t('ADMIN_OWNER.LOCATION_ID')}</dt>
            <dd className="font-mono min-w-0 flex-1 text-[12px] break-all text-ink-200 md:text-[13px]">
              {location.id}
            </dd>
          </div>
        </dl>

        {/* Admin-tool links — open in same tab. "Abrir en admin" is not
              repeated here; it lives in the always-visible summary bar. */}
        {(adminTeamHref || adminUserHref) && (
          <div className="mt-5 flex flex-col gap-2 md:flex-row md:flex-wrap md:gap-3">
            {adminTeamHref && (
              <Link href={adminTeamHref} className={detailLinkClass}>
                <i
                  aria-hidden="true"
                  className="si si-commercial text-[13px]"
                />
                {t('ADMIN_OWNER.VIEW_TEAM')}
              </Link>
            )}
            {adminUserHref && (
              <Link href={adminUserHref} className={detailLinkClass}>
                <i aria-hidden="true" className="si si-user text-[13px]" />
                {t('ADMIN_OWNER.VIEW_HOST')}
              </Link>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}
